<template>
  <div>
    <vx-card>
      <p class="mb-5">
        Information in this section (Introduction) does not need to be learned or memorised and you
        will not be quizzed on it.
      </p>
      <p>
        This information is provided for you to read through and gain an understanding of this
        particular career path before starting your Virtual Job Experience.
      </p>
    </vx-card>

    <vx-card>
      <h4 class="mb-5">Let’s start your virtual job experience in Physiotherapy!</h4>
      <h4 class="mb-5">What they do:</h4>
      <p class="mb-5">
        Physiotherapists assess, treat and prevent disorders in human movement caused by injury or
        disease.
      </p>
      <div class="vx-row">
        <div class="vx-col lg:w-1/2">
          <h4 class="mb-5">What education they need:</h4>
          <p class="mb-5">
            You need a bachelor’s degree in physiotherapy to work as a Physiotherapist. It is also
            common to complete postgraduate studies.
          </p>
          <p class="mb-5">
            Registration with the Australian Health Practitioner Regulation Agency is required.
          </p>
          <p class="mb-5">
            Before starting a course, check it will provide you with the skills and qualifications
            you need. Visit:
          </p>
          <p>
            <a href="https://www.courseseeker.edu.au/" target="_blank">Course Seeker</a> to search
            and compare higher education courses.
          </p>
          <p>
            <a href="https://www.compared.edu.au/" target="_blank">ComparED</a> to compare
            undergraduate and postgraduate student experiences and outcomes.
          </p>
        </div>
        <div class="vx-col lg:w-1/2">
          <centered-image-wrapper
            :image="require('@/assets/images/experiences/physiotherapy/cartoon.jpg')"
            alt='Cartoon captioned "Nursing home in a post-texting world". Elderly residents sit with their heads permanently bowed forward, as though still looking down at a phone.'
            classes="3/3"
          />
          <p class="text-center">
            [Source:
            <a href="https://www.pinterest.com.au/pin/310466968038439242" target="_blank"
              >https://www.pinterest.com.au/pin/310466968038439242]</a
            >
          </p>
        </div>
      </div>
    </vx-card>

    <vx-card>
      <h4 class="mb-5">Quick Details:</h4>

      <div class="vx-row mt-5 flex justify-center">
        <div class="vx-col xl:w-2/3 lg:w-1/2 w-full my-5">
          <ul class="default-list">
            <li><strong>Size:</strong> This is a large occupation.</li>
            <li>
              <strong>Earnings:</strong> Full-time workers on an adult wage earn around $1,444 per
              week (similar to the average of $1,460). Earnings tend to be lower when starting out
              and higher as experience grows.
            </li>
            <li>
              <strong>Full-time:</strong> Many work full-time (63%, similar to the average of 66%).
            </li>
            <li>
              <strong>Hours:</strong> Full-time workers spend around 42 hours per week at work
              (compared to the average of 44 hours).
            </li>
            <li>
              <strong>Age:</strong> The average age is 35 years (compared to the average of 40
              years).
            </li>
            <li>
              <strong>Gender:</strong> 67% of workers are female (compared to the average of 48%).
            </li>
          </ul>

          <h4 class="mb-5">Tasks</h4>
          <ul class="default-list mb-5">
            <li>
              Administering muscle, nerve, joint and functional ability tests to identify and assess
              physical problems of patients
            </li>
            <li>Designing treatment programs to address patients' problems</li>
            <li>
              Treating patients to reduce pain, improve circulation, strengthen muscles, improve
              cardiothoracic, cardiovascular and respiratory functions, restore joint mobility, and
              improve balance and coordination
            </li>
            <li>
              Using the therapeutic properties of exercise, heat, cold, massage, manipulation,
              hydrotherapy, electrotherapy, ultraviolet and infra-red light and ultrasound in the
              treatment of patients
            </li>
            <li>
              Reviewing, continually monitoring, assessing and evaluating programs and treatments
            </li>
            <li>
              Consulting with other Health Professionals as required about patients' problems, needs
              and progress
            </li>
            <li>Instructing patients and their families in procedures to be continued at home</li>
            <li>Recording treatments given and patients' responses and progress</li>
            <li>
              Developing and implementing screening and preventative health promotion programs
            </li>
          </ul>

          <p class="text-grey">
            Source: <a href="https://joboutlook.gov.au/" target="_blank">Job Outlook</a>
          </p>
        </div>
        <div class="vx-col xl:w-1/3 lg:w-1/2 w-full my-5">
          <centered-image-wrapper
            :image="require('@/assets/images/experiences/physiotherapy/physio_stats.png')"
            alt="All Physiotherapists: $1,444 weekly pay, very strong future growth, 31,900 workers, very high skill level rating, 63% full-time, 42 hours average full-time week, average age 35, 67% female."
            classes="3/3"
          />
        </div>
      </div>
      <vs-alert class="mt-5" active="true" title="Caution:" color="dark">
        The Australian jobs market is changing in response to the COVID-19 pandemic. These estimates
        do not take account of the impact of COVID-19. They may not reflect the current jobs market
        and should be used and interpreted with extreme caution.
      </vs-alert>
    </vx-card>

    <vx-card>
      <h4 class="mb-5">Useful Resources</h4>
      <ul class="default-list">
        <li>
          <a href="https://www.ahpra.gov.au/" target="_blank"
            >Australian Health Practitioner Regulation Agency</a
          >
        </li>
        <li>
          <a href="https://australian.physio/" target="_blank"
            >Australian Physiotherapy Association</a
          >
        </li>
        <li>
          <a href="https://ahpa.com.au/" target="_blank">Allied Health Professions Australia</a>
        </li>
      </ul>
      <centered-image-wrapper
        :image="require('@/assets/images/experiences/physiotherapy/cartoon_2.jpg')"
        alt='Cartoon captioned "Yep... looks like you pulled a hamstring". A therapist examines the leg of a pig sitting on a treatment table.'
        classes="3/3"
      />
      <p class="text-center">
        [Source:
        <a href="https://www.pinterest.com.au/pin/310466968048804243" target="_blank"
          >https://www.pinterest.com.au/pin/310466968048804243</a
        >]
      </p>
    </vx-card>
  </div>
</template>

<script>
export default {
  data() {
    return {
      popupActive: false,
      activeTab: 0,
    };
  },
};
</script>
